import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";
import type { VehicleRaffle, RaffleEntry, RaffleStatus } from "@/types/raffle";
import { normalizeRaffle, normalizeRaffleEntry } from "@/types/raffle";

export async function getAllRaffles(): Promise<VehicleRaffle[]> {
  const q = query(
    collection(db, COLLECTIONS.RAFFLES),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) =>
    normalizeRaffle({ id: d.id, ...d.data() } as { id: string } & Record<string, unknown>)
  );
}

export async function getRaffleById(id: string): Promise<VehicleRaffle | null> {
  const ref = doc(db, COLLECTIONS.RAFFLES, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return normalizeRaffle({ id: snap.id, ...snap.data() } as { id: string } & Record<string, unknown>);
}

export async function getRaffleEntries(raffleId: string): Promise<RaffleEntry[]> {
  const q = query(
    collection(db, COLLECTIONS.RAFFLE_ENTRIES),
    where("raffleId", "==", raffleId)
  );
  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map((d) =>
    normalizeRaffleEntry({ id: d.id, ...d.data() } as { id: string } & Record<string, unknown>)
  );
  entries.sort((a, b) => {
    const da = a.entryDate && typeof (a.entryDate as { toDate?: () => Date }).toDate === "function" ? (a.entryDate as { toDate: () => Date }).toDate().getTime() : 0;
    const db_ = b.entryDate && typeof (b.entryDate as { toDate?: () => Date }).toDate === "function" ? (b.entryDate as { toDate: () => Date }).toDate().getTime() : 0;
    return db_ - da;
  });
  return entries;
}

export async function updateRaffleStatus(raffleId: string, status: RaffleStatus): Promise<void> {
  const ref = doc(db, COLLECTIONS.RAFFLES, raffleId);
  const updates: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (status === "cancelled" || status === "completed") {
    updates.isActive = false;
  }
  if (status === "active") {
    updates.isActive = true;
  }
  await updateDoc(ref, updates);
}

/** Realizar sorteo: elige un ganador aleatorio entre las entradas (isVerified o todas si no hay campo) */
export async function drawRaffle(raffleId: string): Promise<{ winningNumber: number; winner: RaffleEntry }> {
  return runTransaction(db, async (transaction) => {
    const raffleRef = doc(db, COLLECTIONS.RAFFLES, raffleId);
    const raffleSnap = await transaction.get(raffleRef);
    if (!raffleSnap.exists()) throw new Error("La rifa no existe");
    const raffleData = raffleSnap.data() as Record<string, unknown>;
    if (raffleData.status !== "active") throw new Error("La rifa no está activa");

    const entriesQuery = query(
      collection(db, COLLECTIONS.RAFFLE_ENTRIES),
      where("raffleId", "==", raffleId)
    );
    const entriesSnap = await getDocs(entriesQuery);
    const entries = entriesSnap.docs.map((d: { id: string; data: () => Record<string, unknown> }) => ({
      id: d.id,
      ...d.data(),
    })) as (RaffleEntry & { id: string })[];
    const verified = entries.filter((e) => (e as unknown as Record<string, unknown>).isVerified !== false);
    const list = verified.length > 0 ? verified : entries;
    if (list.length === 0) throw new Error("No hay entradas para sortear");

    const winner = list[Math.floor(Math.random() * list.length)];
    const winningNumber = winner.selectedNumber;

    transaction.update(raffleRef, {
      status: "completed",
      winningNumber,
      winner: {
        userId: winner.userId,
        userName: winner.userName,
        selectedNumber: winningNumber,
        cedula: winner.cedula,
        announcedAt: serverTimestamp(),
      },
      isActive: false,
      completedAt: serverTimestamp(),
      drawnAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const winnerEntryRef = doc(db, COLLECTIONS.RAFFLE_ENTRIES, winner.id);
    transaction.update(winnerEntryRef, { isWinner: true });

    return { winningNumber, winner };
  });
}
